import React from "react";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useLocation, useNavigate } from "react-router-dom";

import ContentCardList from "../../../Components/ContentCardList/ContentCardList";
import ContentCardsSkeleton from "../../../Components/ContentCardsSkeleton/ContentCardsSkeleton";
import PageHeader from "../../../Components/PageHeader/PageHeader";
import useContentSearch from "../../../hooks/PortalAluno/useContentSearch";
import {
  SEARCH_MIN_LENGTH,
  isSearchable,
} from "../../../services/portalAluno/search/searchService";
import { readSearchQueryParam } from "../../../utils/portalAluno/listingFilters";
import "./styles.css";

const getErrorMessage = (error: unknown) =>
  error instanceof Error && error.message && error.message !== "Network Error"
    ? error.message
    : "Não foi possível pesquisar agora. Verifique sua conexão e tente novamente.";

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = readSearchQueryParam(location.search);
  const { data, error, isError, isFetching, refetch } = useContentSearch(query);

  const sections = data?.sections ?? [];

  if (!isSearchable(query)) {
    return (
      <section className="content-page search-page" aria-label="Pesquisa">
        <PageHeader
          title="Pesquisa"
          description="Digite no campo de busca para encontrar conteúdos, cursos, trilhas, comunicados e treinamentos."
        />
        <p className="search-page__hint" role="status">
          Use pelo menos {SEARCH_MIN_LENGTH} caracteres para pesquisar.
        </p>
      </section>
    );
  }

  return (
    <section
      className="content-page search-page"
      aria-label={`Resultados para ${query}`}
    >
      <PageHeader
        title={`Resultados para “${query}”`}
        description={
          isFetching
            ? "Procurando nos conteúdos disponíveis para o seu perfil…"
            : `${data?.total ?? 0} ${
                (data?.total ?? 0) === 1
                  ? "conteúdo encontrado"
                  : "conteúdos encontrados"
              } para o seu perfil.`
        }
      />

      {isError && !data ? (
        <div className="search-page__feedback" role="alert">
          <h1>Não foi possível concluir a pesquisa</h1>
          <p>{getErrorMessage(error)}</p>
          <button type="button" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      ) : isFetching && !data ? (
        <ContentCardsSkeleton count={6} showTitle={false} />
      ) : sections.length === 0 ? (
        <div className="search-page__feedback" role="status">
          <h1>Nenhum conteúdo encontrado</h1>
          <p>
            Não achamos nada para “{query}”. Tente outra palavra ou navegue
            pelos temas da Home.
          </p>
          <button type="button" onClick={() => navigate("/app")}>
            Voltar para a Home
          </button>
        </div>
      ) : (
        <div className="search-page__sections">
          {sections.map((section) => (
            <div className="search-page__section" key={section.title}>
              <div className="search-page__section-heading">
                <h2>{section.title}</h2>
                {section.route && (
                  <button
                    type="button"
                    onClick={() => navigate(section.route as string)}
                    aria-label={`Ver todos os conteúdos de ${section.title}`}
                  >
                    <span>Ver todos</span>
                    <ChevronRightRoundedIcon aria-hidden="true" />
                  </button>
                )}
              </div>

              <ContentCardList
                title={section.title}
                cards={section.cards}
                hideTitle
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default SearchResultsPage;
